import { ResetPasswordFormSchema } from "@compass/shared/schema/authn";
import { useForm } from "@tanstack/react-form";
import { useSearch } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import eyeClose from "../assets/img/eye-close.png";
import eyeOpen from "../assets/img/eye-open.png";
import logo from "assets/img/compass-logo-login.webp";
import { Button, Input, Spinner, ValidationMessage } from "components/elements";
import { requestResetLink } from "../lib/requestResetLink";
import { API_BASE } from "../config";

const schema = ResetPasswordFormSchema;

export const ResetPassword = () => {
    const { token } = useSearch({ from: "/reset-password" });
    const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
    const [error, setError] = useState<string | null>(null);
    const [email, setEmail] = useState<string | null>(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const [hidePassword, setHidePassword] = useState(true);
    const [hideConfirmPassword, setHideConfirmPassword] = useState(true);

    const form = useForm({
        defaultValues: {
            password: "",
            confirmPassword: "",
        },
        validators: {
            onChange: schema,
            onSubmit: schema,
        },
        onSubmit: async ({ value }) => {
            try {
                const res = await fetch(`${API_BASE}/reset-password`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ token, password: value.password }),
                });

                if (!res.ok) throw new Error("Failed to reset password");
                setStatus("success");
            } catch (err) {
                setError("Your reset link is invalid or expired. Please request a new one.");
                setStatus("error");
            }
        },
    });

    useEffect(() => {
        if (!token) return;

        const fetchUser = async () => {
            try {
                const res = await fetch(`${API_BASE}/reset-password/info?token=${token}`);
                const data = await res.json();

                if (data.email) {
                    setEmail(data.email);
                }

                const errorMsg = (data.error || "").toLowerCase();

                if (errorMsg.includes("expired")) {
                    setError("Token expired. Please request a new reset link.");
                } else if (errorMsg.includes("invalid")) {
                    setError("Invalid or malformed reset token. Contact support for a valid link.");
                } else if (errorMsg.includes("user not found")) {
                    setError("Invalid or malformed reset token. Contact support for a valid link.");
                }
            } finally {
                setLoadingUser(false);
            }
        };

        fetchUser();
    }, [token]);

    const onRequestResetLink = async () => {
        if (!email) {
            setError("Cannot request reset link without a valid email.");
            return;
        }

        try {
            await requestResetLink(email);
            enqueueSnackbar("We've sent a new reset link to your email.", { variant: "success" });
            setStatus("success");
        } catch (err: any) {
            setError(err.message || "Failed to send reset link. Please try again later.");
        }
    };

    const isTokenUsable = error === null;

    if (!token) return <div className="p-4 text-center">Missing reset token.</div>;

    if (loadingUser) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner className="h-10 w-10" />
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#f7fbfc] px-4">
            <div className="w-full max-w-sm">
                <div className="mb-8 text-center">
                    <img src={logo} alt="Compass Logo" className="mx-auto h-20" />
                </div>

                <h1 className="mb-3 text-center text-xl font-bold">Set a New Password</h1>

                {status === "success" ? (
                    <div className="text-center">
                        <p className="mb-2 text-green-600">Password successfully reset.</p>
                        <Link to="/login" className="text-blue-600 underline hover:text-blue-800">
                            Click here to log in
                        </Link>
                    </div>
                ) : (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                        className="space-y-4"
                        method="POST"
                    >
                        {email && (
                            <div>
                                <label className="mb-1 block text-sm text-gray-600">Email</label>
                                <input
                                    type="text"
                                    value={email}
                                    readOnly
                                    className="w-full rounded border bg-gray-100 px-3 py-2 text-sm"
                                />
                            </div>
                        )}

                        {isTokenUsable && (
                            <>
                                <form.Field name="password">
                                    {(field) => (
                                        <div className="relative">
                                            <label
                                                htmlFor="password"
                                                className="absolute -top-2 left-3 z-10 bg-[#f7fbfc] px-1 text-sm text-gray-600"
                                            >
                                                New Password
                                            </label>
                                            <div className="relative">
                                                <input
                                                    name={field.name}
                                                    disabled={!isTokenUsable}
                                                    id="password"
                                                    type={hidePassword ? "password" : "text"}
                                                    value={field.state.value}
                                                    onChange={(e) =>
                                                        field.handleChange(e.target.value)
                                                    }
                                                    onBlur={field.handleBlur}
                                                    className="w-full rounded border border-[#6f7070] px-3 py-2.5 text-sm focus:border-black focus:ring-1 focus:ring-black focus:outline-hidden"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setHidePassword((prev) => !prev)}
                                                    className="absolute inset-y-0 right-0 flex items-center px-3"
                                                >
                                                    <img
                                                        src={hidePassword ? eyeClose : eyeOpen}
                                                        alt={hidePassword ? "Show" : "Hide"}
                                                        className="h-4"
                                                    />
                                                </button>
                                            </div>
                                            <ValidationMessage
                                                error={field.state.meta.errors[0]?.message}
                                            />
                                        </div>
                                    )}
                                </form.Field>

                                <form.Field name="confirmPassword">
                                    {(field) => (
                                        <div className="relative">
                                            <label
                                                htmlFor="confirmPassword"
                                                className="absolute -top-2 left-3 z-10 bg-[#f7fbfc] px-1 text-sm text-gray-600"
                                            >
                                                Confirm Password
                                            </label>
                                            <div className="relative">
                                                <input
                                                    name={field.name}
                                                    id="confirmPassword"
                                                    disabled={!isTokenUsable}
                                                    type={hideConfirmPassword ? "password" : "text"}
                                                    value={field.state.value}
                                                    onChange={(e) =>
                                                        field.handleChange(e.target.value)
                                                    }
                                                    onBlur={field.handleBlur}
                                                    className="w-full rounded border border-[#6f7070] px-3 py-2.5 text-sm focus:border-black focus:ring-1 focus:ring-black focus:outline-hidden"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setHideConfirmPassword((prev) => !prev)
                                                    }
                                                    className="absolute inset-y-0 right-0 flex items-center px-3"
                                                >
                                                    <img
                                                        src={
                                                            hideConfirmPassword ? eyeClose : eyeOpen
                                                        }
                                                        alt={hideConfirmPassword ? "Show" : "Hide"}
                                                        className="h-4"
                                                    />
                                                </button>
                                            </div>
                                            <ValidationMessage
                                                error={field.state.meta.errors[0]?.message}
                                            />
                                        </div>
                                    )}
                                </form.Field>
                            </>
                        )}

                        {error ? (
                            <div className="text-sm text-red-500">
                                <p>{error}</p>
                                {error.includes("Token expired") && (
                                    <Button
                                        onClick={onRequestResetLink}
                                        className="w-full py-3 text-white"
                                    >
                                        Request a new reset link
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <Button type="submit" className="w-full py-3 text-white">
                                Reset Password
                            </Button>
                        )}
                    </form>
                )}
            </div>
        </div>
    );
};
